
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Database } from '@shared/types/db';

type AppRole = Database['public']['Enums']['app_role'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: AppRole[];
  requireAuth?: boolean;
}

export const ProtectedRoute = ({
  children,
  allowedRoles = [],
  requireAuth = true,
}: ProtectedRouteProps) => {
  const { user, userProfile, loading, isProfileComplete } = useAuth();
  const location = useLocation();

  // Show enhanced loading state while checking authentication
  // But only if we don't already have a user and profile (avoid flicker during profile updates)
  if (loading && (!user || !userProfile)) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600 font-medium">Authenticating...</p>
      </div>
    );
  }

  // If authentication is required but user is not logged in
  if (requireAuth && !user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // If user is authenticated but hasn't completed their profile
  if (requireAuth && user && !isProfileComplete) {
    return <Navigate to="/auth/register" state={{ from: location }} replace />;
  }

  // If specific roles are required, check if user has one of them
  if (allowedRoles.length > 0 && userProfile?.role) {
    if (!allowedRoles.includes(userProfile.role)) {
      // Redirect to the user's role dashboard if they're authenticated but not authorized
      return <Navigate to={`/dashboard/${userProfile.role}`} replace />;
    }
  }

  return <>{children}</>;
};
